/**
 * TimeLens API client.
 *
 * Attaches:
 *   - X-Request-ID (per request, fresh UUID)
 *   - Authorization: Bearer <accessToken> when auth store has one
 *
 * 401 handling: the client itself raises ``ApiClientError`` on 401. The
 * auth store / view layer (or wrapping helper) decides whether to refresh
 * the access token and retry once. Keeping refresh orchestration out of
 * the client avoids tangled hook semantics inside ofetch.
 */

import { ofetch, type FetchOptions } from "ofetch";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";
const REFRESH_LOCAL_KEY = "tl.refresh";

export interface ApiError {
  code: string;
  message: string;
  request_id: string;
  details?: Record<string, unknown>;
}

export class ApiClientError extends Error {
  status: number;
  body: ApiError | undefined;

  constructor(status: number, message: string, body?: ApiError) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.body = body;
  }
}

/**
 * 后端常见英文错误 message 到中文的映射。后端服务层抛 ForbiddenError/
 * AuthError/NotFoundError 时使用英文常量，前端展示前先翻译。
 * 匹配规则：把 message 中的标点/多余的子句去除后做大小写不敏感比较。
 */
const ERROR_MESSAGE_ZH: Record<string, string> = {
  "not the memory owner": "不能删除他人创建的记忆",
  "not the album owner": "无权操作该相册（仅相册所有者可操作）",
  "not an album member": "你不是该相册的成员",
  "album member role is viewer":
    "当前是观看人角色，无权编辑相册内容",
  "invite token expired": "邀请链接已过期",
  "invite token exhausted": "邀请链接已用尽",
  "invite token revoked": "邀请链接已被撤销",
  "invite token not found": "邀请链接无效",
  "invite password incorrect": "加入密码错误",
  "album not found": "相册不存在或已被删除",
  "memory not found": "记忆不存在或已被删除",
  "media not found": "媒体文件不存在",
  "media asset not found": "媒体文件不存在",
  "not found": "资源不存在",
  "forbidden": "无权操作",
  "unauthorized": "请先登录",
  // ---- 手机号绑定相关 ----
  "invalid chinese mainland phone number": "手机号格式不正确（需为中国大陆 11 位手机号）",
  "phone already bound to another account": "该手机号已被其他账号绑定",
  "verification code sent recently, please wait before requesting again":
    "验证码已发送，请稍候再试",
  "no verification code for this phone; please request a new one":
    "验证码不存在或已使用，请重新获取",
  "verification code expired; please request a new one":
    "验证码已过期，请重新获取",
  "verification code attempts exhausted; please request a new one":
    "验证码错误次数过多，请重新获取",
  "verification code incorrect": "验证码不正确",
  "tencent sms not configured": "短信服务未配置，请联系管理员",
  "current password is incorrect": "当前密码不正确",
};

function translateBackendMessage(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const normalized = raw.trim().toLowerCase();
  // 精确匹配
  if (ERROR_MESSAGE_ZH[normalized]) return ERROR_MESSAGE_ZH[normalized];
  // 包含匹配（后端 message 可能有 "Detail: xxx" 前缀或附加子句）
  for (const key of Object.keys(ERROR_MESSAGE_ZH)) {
    if (normalized.includes(key)) return ERROR_MESSAGE_ZH[key];
  }
  return raw;
}

function mintRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function readPersistedRefresh(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(REFRESH_LOCAL_KEY);
}

export function writePersistedRefresh(token: string | null): void {
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(REFRESH_LOCAL_KEY, token);
  else localStorage.removeItem(REFRESH_LOCAL_KEY);
}

// ---- access token (set by auth store) ----

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ---- public client ----

export const apiClient = ofetch.create({
  baseURL,
  retry: 0,
  timeout: 15_000,
  onRequest({ options }) {
    const headers = new Headers(options.headers);
    headers.set("X-Request-ID", mintRequestId());
    if (!headers.has("Content-Type") && options.body && !(options.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }
    if (accessToken && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    options.headers = headers;
  },
  onResponseError({ response }) {
    const body = response._data as ApiError | undefined;
    const raw = body?.message ?? response.statusText;
    throw new ApiClientError(
      response.status,
      translateBackendMessage(raw) ?? raw,
      body,
    );
  },
});

export type ApiOptions = FetchOptions<"json">;
